import { z } from 'zod';
import { TagNameSchema, TagSchema } from './tags';

/**
 * `pending-tag-search-saved-views-v2` — AI tag-suggest endpoint
 * (`POST /api/tags/suggest`).
 *
 * Given a flow's name + step titles + optional folder path, the
 * server returns up to 3 tag suggestions ranked by confidence.
 * The suggestions come from a deterministic keyword heuristic
 * when no AI provider is configured; otherwise they're routed
 * through `getEffectiveAiCreds('tagSuggest')` so an operator can
 * pin a cheap provider (Groq is the recommended default — same
 * pattern the contrast-advisor AI fix v1 uses).
 *
 * The wire shape is intentionally narrow:
 *   • `flow_id` is the canonical anchor when the flow already
 *     lives in the DB; the route uses it to backfill known
 *     `appliedTags` so the suggester never re-proposes a tag
 *     the flow already carries.
 *   • `flow_name` + `step_titles` carry the body the heuristic
 *     (or LLM) actually reasons over — kept separate from
 *     `flow_id` so an unsaved draft can still ask for tags.
 *   • `folder_path` is an optional hint — when present, the
 *     heuristic boosts any tag matching the deepest folder
 *     segment (the natural team / suite name in most projects).
 */

export const TagSuggestRequestSchema = z.object({
  /** UUID of the persisted flow (omitted for unsaved drafts). */
  flow_id: z.string().uuid().nullable().optional(),
  /** Flow display name — the strongest single signal. */
  flow_name: z.string().trim().min(1).max(120),
  /**
   * Step titles in order — soft cap at 50 because the typical
   * flow has < 20 steps and a flow with > 50 steps probably
   * exceeds the heuristic's grammar anyway.
   */
  step_titles: z.array(z.string().trim().min(1).max(200)).max(50).default([]),
  /**
   * Optional `folder/sub/leaf` path string. The heuristic
   * matches against the deepest segment first, then walks up.
   */
  folder_path: z.string().max(200).nullable().optional(),
  /**
   * Limit the response to N suggestions. Defaults to 3 (the
   * audience-review spec). The route clamps to [1, 5].
   */
  limit: z.number().int().min(1).max(5).optional(),
});
export type TagSuggestRequest = z.infer<typeof TagSuggestRequestSchema>;

/**
 * One suggested tag. `tag` is the full canonical row if the tag
 * already exists in the org's `tags` table; for brand-new
 * suggestions the row is omitted (the client offers a one-click
 * Create-and-apply CTA in that case).
 */
export const TagSuggestionSchema = z.object({
  /** Canonical lowercase tag name. */
  name: TagNameSchema,
  /** Confidence in `[0, 1]`. */
  confidence: z.number().min(0).max(1),
  /**
   * Short why-string — the chip surfaces this on hover so the
   * reviewer can audit the model's reasoning ("matched folder
   * segment 'auth-team'", "step 'login' keyword", etc).
   */
  reason: z.string().min(1).max(160),
  /** Existing tag row, when the name resolves to a known tag. */
  tag: TagSchema.nullable().optional(),
});
export type TagSuggestion = z.infer<typeof TagSuggestionSchema>;

export const TagSuggestResponseSchema = z.object({
  /** Up to `limit` suggestions, ranked by `confidence DESC`. */
  suggestions: z.array(TagSuggestionSchema),
  /**
   * Provider that served the call. `'heuristic'` means the
   * server fell back to the deterministic keyword path (no
   * AI provider configured for `tagSuggest`, or the provider
   * is `mock` / `local`).
   */
  provider: z.enum(['heuristic', 'openai', 'groq', 'anthropic', 'ollama', 'vllm']),
  /** Whether the deterministic fallback ran. */
  fallback: z.boolean(),
});
export type TagSuggestResponse = z.infer<typeof TagSuggestResponseSchema>;

/**
 * `pending-tag-search-saved-views-v2` — folder → tag one-shot
 * migration proposal.
 *
 * The migration tool scans a project's folder tree and proposes
 * one tag per non-empty leaf folder, with a per-row toggle so
 * the reviewer can opt in / out before accepting. The route
 * lives at `POST /api/tags/migrate-folders/preview` and is
 * paired with `POST /api/tags/migrate-folders/apply` which
 * accepts the same proposal shape (mutated by the reviewer) and
 * fans out the tag creates + flow-tag applies in a transaction.
 *
 * The preview is intentionally a pure server-side computation —
 * no rows are written until apply lands.
 */
export const FolderTagProposalRowSchema = z.object({
  /** Slash-joined folder path (`web/auth/login`). */
  folder_path: z.string().min(1).max(200),
  /** Proposed canonical lowercase tag name. */
  proposed_tag_name: TagNameSchema,
  /** UUIDs of flows under this folder. */
  flow_ids: z.array(z.string().uuid()).min(1).max(200),
  /**
   * `true` when a tag with the proposed name already exists in
   * the org. The reviewer's accept then applies the existing
   * tag rather than minting a new one.
   */
  tag_exists: z.boolean(),
  /**
   * UUID of the existing tag when `tag_exists` is true.
   */
  existing_tag_id: z.string().uuid().nullable(),
});
export type FolderTagProposalRow = z.infer<typeof FolderTagProposalRowSchema>;

export const FolderTagPreviewResponseSchema = z.object({
  rows: z.array(FolderTagProposalRowSchema),
});
export type FolderTagPreviewResponse = z.infer<typeof FolderTagPreviewResponseSchema>;

export const FolderTagApplyRequestSchema = z.object({
  /**
   * The reviewer-accepted rows. The route enforces that every
   * row originated from a recent preview by re-checking the
   * folder structure server-side — if a folder has changed
   * since the preview, that row is dropped with a warning.
   */
  rows: z.array(FolderTagProposalRowSchema).min(1).max(200),
});
export type FolderTagApplyRequest = z.infer<typeof FolderTagApplyRequestSchema>;

export const FolderTagApplyResponseSchema = z.object({
  /** Number of tags created in this transaction. */
  tags_created: z.number().int().nonnegative(),
  /** Number of `flow_tags` rows inserted. */
  flow_tag_applications: z.number().int().nonnegative(),
  /** Folders skipped (folder no longer exists, no flows, etc). */
  skipped: z.array(
    z.object({
      folder_path: z.string(),
      reason: z.string(),
    }),
  ),
});
export type FolderTagApplyResponse = z.infer<typeof FolderTagApplyResponseSchema>;

/**
 * Pure helper — derive a slug-safe tag name from a folder
 * segment. Lowercase, replace non-`[a-z0-9_-]` with `-`, collapse
 * repeated dashes, and trim leading / trailing dashes. Returns
 * `null` when the input collapses to empty (e.g. a folder named
 * `   ` or `///`).
 *
 * Exported because both the server (preview) and the client
 * (folder migration modal preview) need to compute it
 * identically.
 */
export function deriveTagNameFromFolder(segment: string): string | null {
  const lowered = segment.toLowerCase().trim();
  if (lowered.length === 0) return null;
  const slugged = lowered.replace(/[^a-z0-9_-]+/g, '-').replace(/-+/g, '-');
  const trimmed = slugged.replace(/^-+/, '').replace(/-+$/, '');
  if (trimmed.length === 0) return null;
  if (trimmed.length > 40) return trimmed.slice(0, 40).replace(/-+$/, '');
  return trimmed;
}
